import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { BottomSheet } from '@/components/calendar/BottomSheet';
import { useAuth } from '@/contexts/auth-context';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useResponsiveBreakpoints } from '@/hooks/useMediaQuery';
import { useAvailabilityWizard, type GroupRecurringData, type GroupOneTimeData, type NewServiceData } from './hooks/useAvailabilityWizard';
import { StepProgress } from '@/components/ui/step-progress';
import { getStepLabelKey } from './stepLabels';
import { WizardNavigation } from './WizardNavigation';
import { ModeAndServicesStep } from './steps/ModeAndServicesStep';
import { ServicesStep } from './steps/ServicesStep';
import { RecurrencePatternStep } from './steps/RecurrencePatternStep';
import { DateRangeStep } from './steps/DateRangeStep';
import { OneTimeDateTimeStep } from './steps/OneTimeDateTimeStep';
import { ReviewStep } from './steps/ReviewStep';
import { WelcomeStep } from './steps/WelcomeStep';
import { CreateServiceStep } from './steps/CreateServiceStep';
import { TeamMemberStep } from './steps/TeamMemberStep';
import { FirstTimeScheduleStep } from './steps/FirstTimeScheduleStep';
import { FirstTimeReviewStep } from './steps/FirstTimeReviewStep';
import { ServiceTypeStep } from './steps/ServiceTypeStep';
import { GroupServiceStep } from './steps/GroupServiceStep';
import { GroupRecurrenceStep } from './steps/GroupRecurrenceStep';
import { GroupReviewStep } from './steps/GroupReviewStep';
import type {
  RecurringData,
  DateRangeData,
  OneTimeData,
  ServiceOption,
  ProviderOption,
} from '../CreateAvailabilityDialog';

export interface CreateAvailabilityWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateRecurring: (data: RecurringData) => Promise<void>;
  onCreateDateRange: (data: DateRangeData) => Promise<void>;
  onCreateOneTime: (data: OneTimeData) => Promise<void>;
  // Group-class callbacks. When omitted, the group branch of the wizard is
  // hidden — only the existing one-customer flow is reachable.
  onCreateGroupRecurring?: (data: GroupRecurringData) => Promise<void>;
  onCreateGroupOneTime?: (data: GroupOneTimeData) => Promise<void>;
  onCreateService?: (data: NewServiceData) => Promise<ServiceOption>;
  services?: ServiceOption[];
  providers?: ProviderOption[];
  selectedProviderId?: number | null;
  organizationName?: string;
  currency?: string;
  initialStep?: 0 | 1 | 2 | 3 | 4 | 5;
  skipResetOnClose?: boolean;
  initialDateTime?: { date: Date; startTime: string } | null;
  // Where to create a group service when none exists yet (admin only). Without
  // it the disabled group option shows the hint but no link.
  manageServicesHref?: string;
}

export function CreateAvailabilityWizard({
  open,
  onOpenChange,
  onCreateRecurring,
  onCreateDateRange,
  onCreateOneTime,
  onCreateGroupRecurring,
  onCreateGroupOneTime,
  onCreateService,
  services = [],
  providers = [],
  selectedProviderId: initialProviderId,
  organizationName,
  currency = 'EUR',
  initialStep,
  skipResetOnClose,
  initialDateTime,
  manageServicesHref,
}: CreateAvailabilityWizardProps) {
  const { t } = useTranslation('staff');
  const { isMobile } = useResponsiveBreakpoints();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const handleClose = () => {
    onOpenChange(false);
  };

  const wizard = useAvailabilityWizard({
    onCreateRecurring,
    onCreateDateRange,
    onCreateOneTime,
    onCreateGroupRecurring,
    onCreateGroupOneTime,
    onCreateService,
    services,
    providers,
    selectedProviderId: initialProviderId,
    onClose: handleClose,
    currency,
    initialStep,
    initialDateTime,
  });

  // Pull group-flow helpers up so the renderer can reach them. The selected
  // group service drives the duration that feeds the read-only end time.
  const selectedGroupService =
    wizard.isGroupFlow && wizard.groupServiceId !== null
      ? wizard.groupServices.find((s) => s.id === wizard.groupServiceId) ?? null
      : null;
  const selectedProvider =
    wizard.selectedProviderId !== null
      ? wizard.providers.find((p) => p.id === wizard.selectedProviderId) ?? null
      : null;

  // Render the current step content
  const renderStepContent = () => {
    // Blocked state - no active services and can't create one
    if (wizard.noActiveServicesBlocked) {
      return (
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('availability.dialog.noServicesTitle', 'No Active Services')}
          </h3>
          <p className="text-gray-600 mb-6">
            {isAdmin
              ? t('availability.dialog.noServicesMessage', 'You need to have at least one active service before you can create availability. Please go to Services to create or activate a service first.')
              : t('availability.dialog.noServicesMessageStaff', 'You need to have at least one active service assigned to you before you can create availability. Please ask your administrator to assign an active service to you.')
            }
          </p>
          <Button
            variant="outline"
            onClick={handleClose}
          >
            {t('availability.dialog.close', 'Close')}
          </Button>
        </div>
      );
    }

    // First-time flow (no services exist)
    if (wizard.isFirstTimeFlow) {
      switch (wizard.currentStep) {
        case 0: // Welcome
          return <WelcomeStep organizationName={organizationName} />;

        case 1: // Create Service
          return (
            <CreateServiceStep
              serviceName={wizard.newServiceName}
              onServiceNameChange={wizard.setNewServiceName}
              duration={wizard.newServiceDuration}
              onDurationChange={wizard.setNewServiceDuration}
              price={wizard.newServicePrice}
              onPriceChange={wizard.setNewServicePrice}
              currency={wizard.currency}
            />
          );

        case 2: // Team Member
          return (
            <TeamMemberStep
              teamMembers={wizard.providers.map((p) => ({
                id: p.id,
                name: p.name,
                avatar_url: p.avatar_url ?? undefined,
              }))}
              selectedMemberId={wizard.selectedProviderId}
              onSelectMember={(id) => wizard.setSelectedProviderId(id)}
              isFirstTimeFlow
            />
          );

        case 3: // Schedule
          return (
            <FirstTimeScheduleStep
              selectedDays={wizard.selectedDays}
              onSelectedDaysChange={wizard.setSelectedDays}
              startTime={wizard.startTime}
              onStartTimeChange={wizard.setStartTime}
              endTime={wizard.endTime}
              onEndTimeChange={wizard.setEndTime}
            />
          );

        case 4: // Review
          return (
            <FirstTimeReviewStep
              serviceName={wizard.newServiceName}
              serviceDuration={wizard.newServiceDuration}
              servicePrice={wizard.newServicePrice}
              currency={wizard.currency}
              selectedDays={wizard.selectedDays}
              startTime={wizard.startTime}
              endTime={wizard.endTime}
              providerName={
                wizard.providers.find((p) => p.id === wizard.selectedProviderId)?.name
              }
              onEditStep={wizard.goToStep}
            />
          );

        default:
          return null;
      }
    }

    // Normal flow (services exist)
    // Step 0: Service-type picker — group vs one-customer. Skipped when the
    // page hasn't wired the group callbacks (no group flow available).
    if (wizard.currentStep === 0) {
      const groupWired = !!onCreateGroupRecurring && !!onCreateGroupOneTime;
      const hasGroupServices = wizard.groupServices.length > 0;
      const groupEnabled = groupWired && hasGroupServices;
      return (
        <ServiceTypeStep
          value={wizard.serviceType}
          onChange={wizard.setServiceType}
          groupEnabled={groupEnabled}
          groupDisabledHint={
            !groupWired
              ? t(
                  'availability.serviceType.groupComingSoon',
                  'Group classes are coming soon to this view.'
                )
              : !hasGroupServices
                ? t(
                    'availability.serviceType.noGroupServices',
                    'Create a group service first (more than one participant).'
                  )
                : undefined
          }
          groupDisabledLink={
            groupWired && !hasGroupServices && manageServicesHref
              ? {
                  href: manageServicesHref,
                  label: t('availability.serviceType.manageServices', 'Go to services'),
                }
              : undefined
          }
        />
      );
    }

    // Group flow (steps 1-3)
    if (wizard.isGroupFlow) {
      const groupServiceName = selectedGroupService?.name ?? '';
      const groupDuration = selectedGroupService?.duration_minutes ?? 0;

      if (wizard.currentStep === 1) {
        return (
          <GroupServiceStep
            services={wizard.groupServices}
            selectedServiceId={wizard.groupServiceId}
            onSelectService={(id) => wizard.setGroupServiceId(id)}
            providers={wizard.providers.length > 1 ? wizard.providers : undefined}
            selectedProviderId={wizard.selectedProviderId}
            onProviderIdChange={
              wizard.providers.length > 1 ? wizard.setSelectedProviderId : undefined
            }
          />
        );
      }

      if (wizard.currentStep === 2) {
        return (
          <GroupRecurrenceStep
            mode={wizard.groupRecurrenceMode}
            onModeChange={wizard.setGroupRecurrenceMode}
            recurrenceType={wizard.recurrenceType}
            onRecurrenceTypeChange={wizard.setRecurrenceType}
            recurrenceInterval={wizard.recurrenceInterval}
            onRecurrenceIntervalChange={wizard.setRecurrenceInterval}
            monthlyWeek={wizard.monthlyWeek}
            onMonthlyWeekChange={wizard.setMonthlyWeek}
            monthlyDay={wizard.monthlyDay}
            onMonthlyDayChange={wizard.setMonthlyDay}
            selectedDays={wizard.selectedDays}
            onSelectedDaysChange={wizard.setSelectedDays}
            endType={wizard.endType}
            onEndTypeChange={wizard.setEndType}
            endDate={wizard.endDate}
            onEndDateChange={wizard.setEndDate}
            occurrences={wizard.occurrences}
            onOccurrencesChange={wizard.setOccurrences}
            oneTimeDate={wizard.oneTimeDate}
            onOneTimeDateChange={wizard.setOneTimeDate}
            startTime={wizard.startTime}
            onStartTimeChange={wizard.setStartTime}
            durationMinutes={groupDuration}
            serviceName={groupServiceName}
          />
        );
      }

      if (wizard.currentStep === 3) {
        return (
          <GroupReviewStep
            service={selectedGroupService}
            provider={selectedProvider}
            mode={wizard.groupRecurrenceMode}
            selectedDays={wizard.selectedDays}
            recurrenceType={wizard.recurrenceType}
            endType={wizard.endType}
            endDate={wizard.endDate}
            occurrences={wizard.occurrences}
            oneTimeDate={wizard.oneTimeDate}
            startTime={wizard.startTime}
          />
        );
      }

      return null;
    }

    // One-customer flow
    // Step 1: Type Selection + Provider
    if (wizard.currentStep === 1) {
      return (
        <ModeAndServicesStep
          mode={wizard.mode}
          onModeChange={wizard.setMode}
          providers={wizard.providers}
          selectedProviderId={wizard.selectedProviderId}
          onProviderIdChange={wizard.setSelectedProviderId}
        />
      );
    }

    // Step 2: Services — filtered to one-customer only so group services
    // can't sneak into a window-style rule.
    if (wizard.currentStep === 2) {
      return (
        <ServicesStep
          services={wizard.oneCustomerServices}
          selectedServiceIds={wizard.selectedServiceIds}
          onToggleService={wizard.toggleService}
          onSelectAll={() =>
            wizard.setSelectedServiceIds(wizard.oneCustomerServices.map((s) => s.id))
          }
          onDeselectAll={wizard.deselectAllServices}
        />
      );
    }

    // Step 3: Schedule details (varies by mode)
    if (wizard.currentStep === 3) {
      switch (wizard.mode) {
        case 'recurring':
          return (
            <RecurrencePatternStep
              recurrenceType={wizard.recurrenceType}
              onRecurrenceTypeChange={wizard.setRecurrenceType}
              recurrenceInterval={wizard.recurrenceInterval}
              onRecurrenceIntervalChange={wizard.setRecurrenceInterval}
              monthlyWeek={wizard.monthlyWeek}
              onMonthlyWeekChange={wizard.setMonthlyWeek}
              monthlyDay={wizard.monthlyDay}
              onMonthlyDayChange={wizard.setMonthlyDay}
              selectedDays={wizard.selectedDays}
              onSelectedDaysChange={wizard.setSelectedDays}
              startType={wizard.startType}
              onStartTypeChange={wizard.setStartType}
              effectiveFrom={wizard.effectiveFrom}
              onEffectiveFromChange={wizard.setEffectiveFrom}
              endType={wizard.endType}
              onEndTypeChange={wizard.setEndType}
              endDate={wizard.endDate}
              onEndDateChange={wizard.setEndDate}
              occurrences={wizard.occurrences}
              onOccurrencesChange={wizard.setOccurrences}
              startTime={wizard.startTime}
              onStartTimeChange={wizard.setStartTime}
              endTime={wizard.endTime}
              onEndTimeChange={wizard.setEndTime}
            />
          );
        case 'date_range':
          return (
            <DateRangeStep
              startDate={wizard.dateRangeStart}
              onStartDateChange={wizard.setDateRangeStart}
              endDate={wizard.dateRangeEnd}
              onEndDateChange={wizard.setDateRangeEnd}
              startTime={wizard.startTime}
              onStartTimeChange={wizard.setStartTime}
              endTime={wizard.endTime}
              onEndTimeChange={wizard.setEndTime}
              reason={wizard.dateRangeReason}
              onReasonChange={wizard.setDateRangeReason}
            />
          );
        case 'one_time':
          return (
            <OneTimeDateTimeStep
              date={wizard.oneTimeDate}
              onDateChange={wizard.setOneTimeDate}
              startTime={wizard.startTime}
              onStartTimeChange={wizard.setStartTime}
              endTime={wizard.endTime}
              onEndTimeChange={wizard.setEndTime}
            />
          );
      }
    }

    // Step 4: Review (all modes — time is set in step 3 now)
    if (wizard.currentStep === 4) {
      return (
        <ReviewStep
          mode={wizard.mode}
          summary={wizard.summary}
          startTime={wizard.startTime}
          endTime={wizard.endTime}
          services={wizard.services}
          selectedServiceIds={wizard.selectedServiceIds}
          onEditStep={wizard.goToStep}
        />
      );
    }

    return null;
  };

  // Main content that's shared between mobile and desktop
  const wizardContent = (
    <div className="flex flex-col h-full min-h-0">
      {/* Step indicator */}
      {(() => {
        const stepConfig = getStepLabelKey(
          wizard.currentStep,
          wizard.mode,
          wizard.isFirstTimeFlow,
          wizard.isGroupFlow,
        );
        const label = stepConfig?.active
          ? t(`availability.${stepConfig.active}`, stepConfig.active.split('.').pop() || '')
          : undefined;
        return (
          <div className="px-6 border-b">
            <StepProgress
              currentStep={wizard.currentStep + 1}
              totalSteps={wizard.totalSteps}
              label={label}
            />
          </div>
        );
      })()}

      {/* Step content - flex-1 with min-h-0 allows scrolling */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {renderStepContent()}
      </div>

      {/* Navigation - hide when blocked */}
      {!wizard.noActiveServicesBlocked && (
        <WizardNavigation
          canGoBack={wizard.canGoBack}
          canGoNext={wizard.canGoNext}
          isLastStep={wizard.isLastStep}
          isSubmitting={wizard.isSubmitting}
          onBack={wizard.goBack}
          onNext={wizard.goNext}
          onSubmit={wizard.submit}
        />
      )}
    </div>
  );

  // Dialog title based on flow type
  const dialogTitle = wizard.isFirstTimeFlow
    ? t('availability.wizard.firstTime.title', 'Get Started')
    : t('availability.dialog.title', 'Add Availability');

  // Mobile: Use BottomSheet
  if (isMobile) {
    return (
      <BottomSheet
        isOpen={open}
        onClose={() => {
          if (!skipResetOnClose) wizard.reset();
          handleClose();
        }}
        title={dialogTitle}
        maxHeight="90dvh"
        showDragHandle
      >
        {wizardContent}
      </BottomSheet>
    );
  }

  // Desktop: Use Dialog
  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen && !skipResetOnClose) {
          wizard.reset();
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[90dvh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex flex-col">
          {wizardContent}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Re-export types for convenience
export type {
  RecurringData,
  DateRangeData,
  OneTimeData,
  ServiceOption,
  ProviderOption,
  NewServiceData,
  GroupRecurringData,
  GroupOneTimeData,
};
